import React from 'react';
import moment from 'moment';
import { fromJS, List } from 'immutable';
import { useSelector } from 'react-redux';
import { InjectedFormProps } from 'redux-form';
import { Field, Fields, FieldArray } from 'redux-form/immutable';
import { FormLabel, FormControlLabel, Radio, RadioGroup, Tooltip, Grid } from '@material-ui/core';

import * as validators from 'utils/validators';
import FormCheckbox from 'retired/FormCheckbox';
import FormTextField from 'retired/FormTextField';
import FormRadioButton from 'retired/FormRadioButton';
import FormCurrencySelect from 'retired/FormCurrencySelect';
import { selectCampaigns } from 'entities/campaign/selectors';
import FormLocationSelector from 'retired/FormLocationSelector';
import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import FormSectionsSortSelector from 'retired/FormSectionsSortSelector';
import FormIncludeExcludeSelector from 'retired/FormIncludeExcludeSelector';
import FormSelectBoxEntitySelector from 'retired/FormSelectBoxEntitySelector';
import { selectAccessibleUserLocations, selectUser } from 'entities/user/selectors';
import { State } from 'reducers';

import { type FormData, valueSelector } from './types';

enum CampaignDates {
  CampaignStart = 'startingAt',
  CampaignEnd = 'endingAt',
}

enum DeductionDates {
  PledgedDonationStart = 'pledgedDonationStartingAt',
  PledgedDonationEnd = 'pledgedDonationEndingAt',
}

// Styles
const labelStyle = {
  color: 'rgba(0, 0, 0, 0.54)',
  margin: '0 0 10px 0',
  fontSize: '0.6964285714285714rem',
  lineHeight: '1em',
};

const isNumber = validators.isNumber();
const notEmpty = validators.notEmpty();

const startEndDateValidation = (
  value: moment.Moment,
  _allValues?: any,
  _props?: any,
  name?: CampaignDates
): string | undefined => {
  if (name === CampaignDates.CampaignEnd) {
    return undefined;
  }

  return !value || !moment(value).isValid()
    ? 'Please select a valid "Campaign Start Date"'
    : undefined;
};

const deductionDateValidation = (
  value: moment.Moment,
  _allValues?: any,
  _props?: any,
  name?: DeductionDates,
  selectedCampaignStartingAt?: moment.Moment
): string | undefined => {
  if (!value || !selectedCampaignStartingAt) {
    return undefined;
  }

  if (name === DeductionDates.PledgedDonationStart) {
    if (
      selectedCampaignStartingAt?.isValid() &&
      moment(value).isBefore(selectedCampaignStartingAt)
    ) {
      return 'Invalid';
    }
    return !value || !moment(value).isValid() ? 'Required' : undefined;
  }

  return undefined;
};

const validateLocations = (value: List<string>, entity: any): string | undefined => {
  if (entity && !entity.get('locationsExclude') && !value?.size) {
    return 'You selected to only include certain locations and none are selected';
  }
  return undefined;
};

const selectedDeductionStartingAtValidationMessage = (
  selectedDeductionStartingAt: moment.Moment | undefined,
  submitFailed: boolean,
  selectedCampaignStartingAt?: moment.Moment
) => {
  if (
    selectedDeductionStartingAt &&
    selectedCampaignStartingAt &&
    moment(selectedDeductionStartingAt).isBefore(selectedCampaignStartingAt)
  ) {
    return 'The Campaign\'s "Deduction Start Date" cannot be before the Campaign\'s "Start Date"';
  }

  if (!selectedDeductionStartingAt && submitFailed) {
    return 'Please select a valid "Deduction Start Date"';
  }

  return undefined;
};

const isOptionalNumber = (value?: string | number): string | undefined =>
  !value ? undefined : isNumber(value);

export const GeneralSettingsSection = ({
  parentProps,
  isPledge,
}: {
  parentProps: InjectedFormProps<FormData>;
  isPledge?: boolean;
}): JSX.Element => {
  const { submitFailed } = parentProps;
  const campaigns = useSelector(selectCampaigns);
  const campaignsSize = campaigns ? campaigns.size : 0;
  const selectedCampaignStartingAt = useSelector((state: State) =>
    valueSelector(state, CampaignDates.CampaignStart)
  );
  const selectedDeductionStartingAt = useSelector((state: State) =>
    valueSelector(state, DeductionDates.PledgedDonationStart)
  );

  const user = useSelector(selectUser);
  const locations = useSelector(selectAccessibleUserLocations);
  const sorting = useSelector((state: State) => valueSelector(state, 'sorting'));
  const privacy = useSelector((state: State) => valueSelector(state, 'privacy'));

  const ensureCompanyOnlyCampaigns = user?.organization?.hasSetting('ensureCompanyOnlyCampaigns');

  const shareTooltip =
    'Select “Share with your Organization” to allow subsidiaries of your parent organization to add this campaign to their subsidiary, thus making it visible to their employees.';

  const publicToolTip =
    "Selecting 'Public' will enable any individual to view and participate in this campaign, without being required to login.";

  const companyToolTip =
    "Selecting 'Company Only' will require users to login to verify they are company employees before they can view or participate in this campaign.";

  const radioShareWithOtherOrganizations = (
    <>
      <Tooltip title={shareTooltip}>
        <FormControlLabel
          control={<Radio />}
          value="shared"
          label="Share with other Organizations"
          checked={privacy === 'shared'}
        />
      </Tooltip>
    </>
  );

  const radioShareWithCompany = (
    <>
      <Tooltip title={companyToolTip}>
        <FormControlLabel
          control={<Radio />}
          value="company"
          label="Company Only"
          checked={privacy === 'company'}
        />
      </Tooltip>
    </>
  );

  const radioShareWithPublic = (
    <>
      <Tooltip title={publicToolTip}>
        <FormControlLabel
          control={<Radio />}
          value="public"
          label="Public"
          checked={privacy === 'public'}
          disabled={ensureCompanyOnlyCampaigns}
        />
      </Tooltip>
    </>
  );

  const campaignSharingRadioButtons: JSX.Element[] = [];

  // If it's a giving pledge we don't want to display the ability to toggle sharing
  // That is the way we're limiting the current feature since payroll is currently the only way to contribute to a giving pledge
  if (!isPledge) {
    campaignSharingRadioButtons.push(radioShareWithCompany, radioShareWithPublic);

    if (user?.organization?.parent) {
      campaignSharingRadioButtons.push(radioShareWithOtherOrganizations);
    }
  }

  return (
    <>
      <div style={{ marginBottom: 40 }}>
        <p style={{ marginBottom: 5 }}>Goal</p>
        <p style={labelStyle}>
          If left empty, the goals of the selected fundraisers will be summed and incorporated into
          the campaign goal
        </p>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Field
              name="goalAmount"
              component={FormTextField}
              type="number"
              inputProps={{ min: '0', step: 'any' }}
              validate={[isOptionalNumber]}
              autoComplete="off"
              label="Goal amount"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Field name="goalCurrencyCode" component={FormCurrencySelect} label="Currency" />
          </Grid>
        </Grid>
        <div style={{ marginTop: 10 }}>
          <Field
            name="hideProgressBar"
            component={FormCheckbox}
            label="Hide the campaign progress bar"
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <Field
            name="hideActivityFeed"
            component={FormCheckbox}
            label="Hide the campaign activity feed and the supporters list"
          />
        </div>
      </div>
      {campaignSharingRadioButtons.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <p>Visibility *</p>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Field name="privacy" component={FormRadioButton}>
                <RadioGroup row>{campaignSharingRadioButtons}</RadioGroup>
              </Field>
            </Grid>
            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: 30 }}>
                <FormIncludeExcludeSelector
                  name="locationsExclude"
                  labelText="the following locations:"
                />
                <Field
                  name="locations"
                  component={FormLocationSelector}
                  props={{ locations }}
                  validate={validateLocations}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      )}
      <div style={{ marginBottom: 20 }}>
        <p>Campaign Start &amp; End Date *</p>
        <Fields
          names={[CampaignDates.CampaignStart, CampaignDates.CampaignEnd]}
          component={FormDateWithStartAndEnd}
          displayTimeAndTimeZone
          label="Campaign Start &amp; End Date"
          future
          fullWidth
          allowEmpty={[false, true]}
          validate={startEndDateValidation}
        />
      </div>
      {isPledge && (
        <div style={{ marginBottom: 20 }}>
          <p>Deduction Start &amp; End Date</p>
          <Fields
            names={[DeductionDates.PledgedDonationStart, DeductionDates.PledgedDonationEnd]}
            component={FormDateWithStartAndEnd}
            displayTimeAndTimeZone
            label="Deduction Start &amp; End Date"
            future
            fullWidth
            allowEmpty={[false, true]}
            validate={deductionDateValidation}
          />

          <Field
            name="allowOneTimePledge"
            component={FormCheckbox}
            label="Allow one-time pledges"
          />

          <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
            {selectedDeductionStartingAtValidationMessage(
              selectedDeductionStartingAt,
              submitFailed,
              selectedCampaignStartingAt
            )}
          </FormLabel>
        </div>
      )}
      <div style={{ marginBottom: 20 }}>
        <p style={{ marginBottom: 5 }}>Position *</p>
        <p style={labelStyle}>
          Select the position in which this campaign will be shown on the homepage along with the
          other campaigns for employees. EX: If you have 3 campaigns published and you select 1,
          then this campaign will be shown first on the homepage
        </p>
        <Field
          name="sorting"
          component={FormSelectBoxEntitySelector}
          options={fromJS(
            Array.from(Array(campaignsSize + 1).keys()).map((item) => ({
              id: (Number(item) + 1) * -1,
              name: Number(item) + 1,
            }))
          )}
          placeholder="Position"
          style={{ width: '100%' }}
          validate={notEmpty}
        />
        {!sorting && submitFailed && (
          <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
            Please select a position
          </FormLabel>
        )}
      </div>
      <div style={{ marginBottom: 10 }}>
        <Field
          name="linkOnly"
          component={FormCheckbox}
          label="Link only: only users with access to the link can view"
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <Field
          name="contentLocationFiltering"
          component={FormCheckbox}
          label="Add a location filter to the campaign"
        />
      </div>
      <div style={{ marginBottom: 20 }}>
        <p style={{ marginBottom: 5 }}>Select the order of Deed opportunity sections</p>
        <p
          style={{
            color: 'rgba(0, 0, 0, 0.54)',
            margin: '0 0 10px 0',
            fontSize: '0.7rem',
            lineHeight: '1em',
          }}
        >
          You may choose to select the order of opportunities sections in which they appear on the
          campaign. If you choose not to change it, the default order of sections will be as follows
          as shown below. Drag them up and down.
        </p>
        <FieldArray
          name="contentSortingSections"
          component={FormSectionsSortSelector}
          props={parentProps}
        />
      </div>
    </>
  );
};
